import React, { useCallback, useEffect, useState } from "react";
import { Num } from "./ui/Num";
import { FilterBuilder, asOfText } from "./FilterBuilder";
import { describeRule } from "./FilterRuleRow";
import { useBroadcastFilters, useFilterPreview } from "./useBroadcastFilters";
import { presetsFromPayload } from "./broadcastFilterDecode";
import { messageFor } from "./broadcastErrors";
import { get as commsGet, post as commsPost } from "../../../service/comms/commsApi";
import {
  listFilterPresets,
  installDefaultFilters,
} from "../../../service/comms/broadcastsService";
import { useCompanyId } from "../../../hooks/comms/useCompanyId";

/**
 * The saved-filter chooser for ONE channel's `filtered` segment.
 *
 * ---------------------------------------------------------------------------
 * THIS ONE IS A CONTAINER
 * ---------------------------------------------------------------------------
 * Unlike `FilterRuleRow` and `FilterBuilder`, which are purely presentational
 * and take everything as props, this component FETCHES. It owns
 * `useBroadcastFilters()` for the saved list and the field registry, a small
 * local hook for the preset catalogue, and `useFilterPreview` for the selected
 * filter's count. That is deliberate: the composer should be able to drop one
 * element into an audience block and get a working picker, not thread six
 * pieces of filter state through a screen that has nothing else to do with
 * filters.
 *
 * ---------------------------------------------------------------------------
 * THE RULE THAT MATTERS MOST HERE: HONOUR `backedByData`
 * ---------------------------------------------------------------------------
 * A preset with `backedByData: false` is one the server will happily install
 * and which will then match nobody, because the table its rules read is empty.
 * `missingDataFor` names exactly which fields those are.
 *
 * Rendered plainly, that preset is a row saying "Lapsed members — 0". And the
 * natural reading of a zero is "the filters are broken", not "that data has not
 * arrived yet". One of those sends someone to raise a bug against a feature
 * that works; the other tells them the truth. So an unbacked preset renders
 * VISIBLY MUTED and NAMES ITS MISSING FIELDS, every time. This is the single
 * line in this file that stops a working feature from looking broken, and it is
 * why the presets are shown at all rather than filtered down to the useful
 * ones — a preset that silently disappears teaches nobody anything.
 *
 * ---------------------------------------------------------------------------
 * `channel` ONLY CHANGES EMPHASIS
 * ---------------------------------------------------------------------------
 * One filter serves both channels. `channel` decides which reachability number
 * leads, never which filters are offered — there is no such thing as a
 * push-only filter, and pretending otherwise would make the same saved filter
 * look like two different objects.
 */

/**
 * How many filters `install-defaults` ships.
 *
 * Named rather than derived from the fetched catalogue so the button reads the
 * same before the catalogue lands as after it — a button whose label changes
 * from "Install the defaults" to "Install the 13 defaults" a moment later looks
 * like it changed its mind about what it will do.
 */
const DEFAULT_FILTER_COUNT = 13;

const BUTTON_BASE =
  "inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-2";

const PRIMARY_BUTTON = "bg-orange-primary text-white hover:bg-[#E85A10]";

const GHOST_BUTTON = "border border-bc-border text-bc-text-2 hover:bg-bc-subtle";

const DISABLED_BUTTON = "cursor-not-allowed opacity-50";

/** The muting applied to a preset whose data has not arrived. */
const MUTED_PRESET = "opacity-60";

/* ── The preset catalogue ─────────────────────────────────────────────── */

/**
 * The preset catalogue and the install action.
 *
 * Local rather than folded into `useBroadcastFilters` because presets are a
 * cold-start concern: they are fetched only when the company has no saved
 * filters at all, and making every render of the picker pay for a counted
 * catalogue it will not show would be a real cost — `with_counts` makes the
 * server count thirteen filters.
 *
 * @param {boolean} enabled - Fetch only when there is nothing saved yet.
 * @returns {{presets: Object[], loading: boolean, error: string|null, installing: boolean, install: () => Promise<boolean>}}
 */
/** companyId -> the decoded catalogue, for the session. Failures excluded. */
const presetCache = new Map();

/** companyId -> the catalogue request in flight, so two mounts share one. */
const pendingPresetLoads = new Map();

/**
 * Fetch the catalogue at most once per company.
 *
 * Same reasoning as the saved-list cache in `useBroadcastFilters.js`, and the
 * cost here is higher per request: `with_counts` makes the server count
 * thirteen filters, and the picker unmounts on every segment click.
 *
 * @param {string} companyId
 * @returns {Promise<Object[]>}
 */
const loadPresets = (companyId) => {
  const inFlight = pendingPresetLoads.get(companyId);
  if (inFlight) return inFlight;

  const promise = listFilterPresets(commsGet, { companyId, withCounts: true })
    .then((payload) => {
      const value = presetsFromPayload(payload).presets;
      pendingPresetLoads.delete(companyId);
      presetCache.set(companyId, value);
      return value;
    })
    .catch((err) => {
      // A failure is never cached — it would stick for the session.
      pendingPresetLoads.delete(companyId);
      throw err;
    });

  pendingPresetLoads.set(companyId, promise);
  return promise;
};

/** Empty the catalogue cache. Called after an install, and by tests. */
const clearPresetCache = () => {
  presetCache.clear();
  pendingPresetLoads.clear();
};

const useFilterPresets = (enabled) => {
  const { companyId } = useCompanyId();

  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [installing, setInstalling] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    // Same company-id gate as every other comms hook: asking with a null one
    // answers with an empty list rather than an error, which would render as
    // "there are no presets" to a company that has thirteen.
    if (!enabled || companyId === null) return undefined;

    const cached = presetCache.get(companyId);
    if (cached) {
      setPresets(cached);
      setError(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    loadPresets(companyId)
      .then((value) => {
        if (cancelled) return;
        setPresets(value);
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setPresets([]);
        // Never `err.message` — `messageFor` decides what a status says.
        setError(messageFor(err));
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, companyId, reloadToken]);

  const install = useCallback(async () => {
    setInstalling(true);
    setError(null);
    try {
      await installDefaultFilters(commsPost, { companyId });
      return true;
    } catch (err) {
      setError(messageFor(err));
      return false;
    } finally {
      setInstalling(false);
      // The counts the catalogue is showing describe a company with nothing
      // installed. Whatever happens next, they are no longer the answer.
      clearPresetCache();
      setReloadToken((token) => token + 1);
    }
  }, [companyId]);

  return { presets, loading, error, installing, install };
};

/**
 * One preset row.
 *
 * @param {Object} props
 * @param {Object} props.preset - Decoded preset: `{name, requires, backedByData, missingDataFor, matched, ...}`.
 * @param {'push'|'email'} props.channel
 * @returns {React.ReactElement}
 */
const PresetRow = ({ preset, channel }) => {
  const backed = preset.backedByData !== false;
  const missing = Array.isArray(preset.missingDataFor) ? preset.missingDataFor : [];
  const reachable =
    channel === "email" ? preset.emailReachable : preset.pushReachable;

  return (
    <li
      data-testid={`bc-preset-${preset.name}`}
      data-backed={backed ? "true" : "false"}
      className={`flex items-baseline justify-between gap-3 rounded-lg border border-bc-border px-3 py-2 ${
        backed ? "" : MUTED_PRESET
      }`}
    >
      <div className="min-w-0">
        <div className="text-[13px] text-bc-text">{preset.name}</div>
        {/* THE LINE. Without it a zero reads as a broken filter. */}
        {backed ? null : (
          <div className="mt-0.5 text-xs text-bc-warn">
            {`Needs ${missing.join(", ")}, which hasn't been collected yet`}
          </div>
        )}
      </div>
      <div className="shrink-0 text-xs text-bc-muted">
        <Num value={preset.matched} className="text-xs" /> match ·{" "}
        <Num value={reachable} className="text-xs" /> reachable
      </div>
    </li>
  );
};

/* ── The picker ───────────────────────────────────────────────────────── */

/**
 * Choose the saved filter a channel's `filtered` segment resolves to.
 *
 * A CONTAINER — it fetches its own filters, fields and presets. See the header.
 *
 * @param {Object} props
 * @param {string|null} [props.value] - The chosen filter's id, or null.
 * @param {(id: string) => void} [props.onChange] - Called with the chosen filter's id.
 * @param {'push'|'email'} [props.channel='push'] - Decides which reachability number leads. Never which filters are offered.
 * @param {string} [props.className] - Extra classes, merged last.
 * @returns {React.ReactElement}
 */
export const FilterPicker = ({
  value = null,
  onChange,
  channel = "push",
  className = "",
}) => {
  const { filters, fields, loading, error, saving, create, update } =
    useBroadcastFilters();

  const [editing, setEditing] = useState(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  /**
   * The failure from the last save attempt.
   *
   * Held separately from the hook's list-level `error` because the builder is
   * an OVERLAY: a banner rendered by the picker sits behind it, so a rejected
   * create looked like the Save button simply doing nothing. That is the worst
   * possible failure mode for a form — the author cannot tell a broken
   * endpoint from a validation problem from a misclick.
   */
  const [saveError, setSaveError] = useState(null);

  /*
   * ONLY ACTIVE FILTERS ARE OFFERED.
   *
   * `is_active: false` is a RETIRED filter — deleting one is a soft delete, so
   * the list route keeps handing it back. Offering it builds a broadcast
   * against rules the company has explicitly stopped using, and the author has
   * nothing on the row to tell them apart. `filterFromRecord` defaults the flag
   * to true when the backend sends none, so a server that does not model
   * retirement at all still shows everything it has.
   */
  const list = (Array.isArray(filters) ? filters : []).filter(
    (entry) => entry.isActive !== false
  );
  const hasSaved = list.length > 0;
  const selected = list.find((entry) => entry.id === value) || null;
  /*
   * The draft points at a filter that is not on offer — retired since it was
   * chosen, or deleted in another tab. Silently clearing it would drop the
   * author's choice with no trace; leaving it unsaid sends a broadcast whose
   * audience is a 422 waiting to happen. So it is said out loud.
   */
  const danglingValue = Boolean(value) && selected === null && !loading;

  const presets = useFilterPresets(!loading && !hasSaved);

  // The selected filter's own count, from the same route the builder previews
  // with. Its rules ARE the question, so there is no second code path and no
  // way for a saved filter's count to disagree with the count it showed while
  // it was being written.
  const { preview } = useFilterPreview(selected?.rules || []);

  const openNew = () => {
    setEditing(null);
    setSaveError(null);
    setBuilderOpen(true);
  };

  const openEdit = () => {
    setEditing(selected);
    setSaveError(null);
    setBuilderOpen(true);
  };

  const handleSave = async (body) => {
    setSaveError(null);
    let saved;
    try {
      saved = editing?.id ? await update(editing.id, body) : await create(body);
    } catch (err) {
      // The hook resolves rather than rejects on a handled failure, but a
      // throw here must not leave the modal looking idle either.
      setSaveError(messageFor(err));
      return;
    }
    if (!saved) {
      // The hook turned the failure into copy on its own `error`, which this
      // overlay covers — so surface it here, and never close the modal on a
      // save that did not happen.
      setSaveError(error || "That filter could not be saved.");
      return;
    }
    setBuilderOpen(false);
    if (saved.id && onChange) onChange(saved.id);
  };

  const builder = (
    <FilterBuilder
      open={builderOpen}
      filter={editing}
      fields={fields}
      saving={saving}
      error={saveError}
      onSave={handleSave}
      onCancel={() => setBuilderOpen(false)}
    />
  );

  if (loading) {
    return (
      <div className={className} data-testid="bc-filter-picker">
        <p className="text-[13px] text-bc-soft">Loading filters…</p>
      </div>
    );
  }

  return (
    <div className={className} data-testid="bc-filter-picker">
      {error ? (
        <div role="alert" className="mb-2 text-xs text-bc-bad">
          {error}
        </div>
      ) : null}

      {danglingValue ? (
        <div
          role="alert"
          data-testid="bc-filter-unavailable"
          className="mb-2 text-xs text-bc-warn"
        >
          The filter this broadcast pointed to is no longer available. Choose
          another.
        </div>
      ) : null}

      {hasSaved ? (
        <>
          <div role="radiogroup" aria-label="Saved filter" className="flex flex-col gap-1">
            {list.map((entry) => (
              <label
                key={entry.id}
                className="flex items-center gap-2 text-[13px] text-bc-text"
              >
                <input
                  type="radio"
                  name="bc-saved-filter"
                  aria-label={entry.name || entry.id}
                  checked={value === entry.id}
                  onChange={() => onChange?.(entry.id)}
                />
                <span>{entry.name}</span>
              </label>
            ))}
          </div>

          {selected ? (
            <div
              data-testid="bc-filter-summary"
              className="mt-2 rounded-lg border border-bc-border bg-bc-subtle p-3"
            >
              <p className="text-xs text-bc-text-2">
                {selected.rules
                  .map((rule) => describeRule(rule, fields))
                  .filter(Boolean)
                  .join(" · ")}
              </p>
              <p className="mt-1.5 text-xs text-bc-muted">
                <Num value={preview?.matched} className="text-xs" /> people match
                ·{" "}
                <Num
                  value={
                    channel === "email"
                      ? preview?.emailReachable
                      : preview?.pushReachable
                  }
                  className="text-xs"
                />{" "}
                {channel === "email" ? "can receive an email" : "can receive a push"}{" "}
                <span data-testid="bc-filter-summary-as-of" className="text-bc-faint">
                  {asOfText(preview?.refreshedAt)}
                </span>
              </p>
            </div>
          ) : null}
        </>
      ) : (
        /*
         * COLD START. No saved filters, so the catalogue leads — an empty
         * picker with a "New filter" button asks someone to invent from
         * scratch what the server already ships thirteen answers to.
         */
        <div data-testid="bc-preset-catalogue">
          <p className="text-[13px] text-bc-text-2">
            No saved filters yet. These thirteen cover the common audiences.
          </p>

          {presets.error ? (
            <div role="alert" className="mt-2 text-xs text-bc-bad">
              {presets.error}
            </div>
          ) : null}

          {presets.loading ? (
            <p className="mt-2 text-xs text-bc-soft">Loading presets…</p>
          ) : (
            <ul className="mt-2 flex flex-col gap-1">
              {presets.presets.map((preset) => (
                <PresetRow key={preset.name} preset={preset} channel={channel} />
              ))}
            </ul>
          )}

          <button
            type="button"
            onClick={presets.install}
            disabled={presets.installing}
            className={`${BUTTON_BASE} ${PRIMARY_BUTTON} mt-3 ${
              presets.installing ? DISABLED_BUTTON : ""
            }`}
          >
            {presets.installing
              ? "Installing…"
              : `Install the ${DEFAULT_FILTER_COUNT} defaults`}
          </button>
        </div>
      )}

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={openNew}
          className={`${BUTTON_BASE} ${GHOST_BUTTON}`}
        >
          New filter
        </button>
        <button
          type="button"
          onClick={openEdit}
          disabled={!selected}
          className={`${BUTTON_BASE} ${GHOST_BUTTON} ${selected ? "" : DISABLED_BUTTON}`}
        >
          Edit
        </button>
      </div>

      {builder}
    </div>
  );
};

export default FilterPicker;
